import { get, limitToLast, query, ref } from 'firebase/database'
import { db } from './firebase'

export const LOG_FETCH_LIMIT = 1440

export const RANGE_CONFIG = {
  hour: {
    label: 'Hour',
    durationMs: 60 * 60 * 1000,
    bucketMs: 60 * 1000,
    bucketLabel: '1-minute',
  },
  day: {
    label: 'Day',
    durationMs: 24 * 60 * 60 * 1000,
    bucketMs: 5 * 60 * 1000,
    bucketLabel: '5-minute',
  },
  week: {
    label: 'Week',
    durationMs: 7 * 24 * 60 * 60 * 1000,
    bucketMs: 60 * 60 * 1000,
    bucketLabel: 'hourly',
  },
  month: {
    label: 'Month',
    durationMs: 30 * 24 * 60 * 60 * 1000,
    bucketMs: 24 * 60 * 60 * 1000,
    bucketLabel: 'daily',
  },
}

export const LOG_CHART_DIMENSIONS = {
  width: 1080,
  height: 320,
  left: 62,
  right: 18,
  top: 18,
  bottom: 42,
}

export function toNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const parsed = Number(value.trim())

    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function getLogPayload(log = {}) {
  if (!log || typeof log !== 'object') {
    return {}
  }

  return log.data && typeof log.data === 'object' ? log.data : log
}

export function getTimestampMs(timestamp) {
  const numericTimestamp = toNumber(timestamp)

  if (numericTimestamp === null) {
    return null
  }

  return numericTimestamp < 100000000000 ? numericTimestamp * 1000 : numericTimestamp
}

export function formatTimestamp(timestamp) {
  const timestampMs = getTimestampMs(timestamp)

  return timestampMs === null ? 'N/A' : new Date(timestampMs).toLocaleString()
}

export function getPowerImport(log) {
  const payload = getLogPayload(log)

  return toNumber(payload.powerImport ?? payload.gridImport ?? payload.import ?? payload.importPower ?? log?.devicedata?.meter?.import?.W)
}

export function getPowerExport(log) {
  const payload = getLogPayload(log)

  return toNumber(payload.powerExport ?? payload.gridExport ?? payload.export ?? payload.exportPower ?? log?.devicedata?.meter?.export?.W)
}

export function formatPowerValue(value) {
  const numericValue = toNumber(value)

  return numericValue === null ? 'N/A' : numericValue.toFixed(2)
}

export function normalizeFirebaseLogs(rawLogs) {
  const logs = Array.isArray(rawLogs)
    ? rawLogs
        .map((value, index) => {
          if (!value || typeof value !== 'object') {
            return null
          }

          return {
            _key: String(index),
            timestamp: value.timestamp ?? value.ts ?? value.time ?? index,
            ...value,
          }
        })
        .filter(Boolean)
    : Object.entries(rawLogs || {}).map(([key, value]) => ({
        _key: key,
        timestamp: value?.timestamp ?? value?.ts ?? value?.time ?? key,
        ...(value && typeof value === 'object' ? value : {}),
      }))

  return logs
    .map((log) => ({
      ...log,
      timestamp: getTimestampMs(log.timestamp),
    }))
    .filter((log) => Number.isFinite(log.timestamp))
    .sort((a, b) => Number(a.timestamp) - Number(b.timestamp))
}

export async function fetchDeviceLogs(deviceId) {
  if (!deviceId) {
    return []
  }

  const logsRef = query(ref(db, `EMS/Logs/${deviceId}/data`), limitToLast(LOG_FETCH_LIMIT))
  const snapshot = await get(logsRef)

  return snapshot.exists() ? normalizeFirebaseLogs(snapshot.val()) : []
}

export function averageLogsByBucket(sourceLogs, bucketMs) {
  return Object.values(
    sourceLogs.reduce((accumulator, log) => {
      const bucketTimestamp = Math.floor(Number(log.timestamp) / bucketMs) * bucketMs

      if (!accumulator[bucketTimestamp]) {
        accumulator[bucketTimestamp] = {
          timestamp: bucketTimestamp,
          powerImportSum: 0,
          powerImportCount: 0,
          powerExportSum: 0,
          powerExportCount: 0,
        }
      }

      const powerImport = getPowerImport(log)
      const powerExport = getPowerExport(log)

      if (powerImport !== null) {
        accumulator[bucketTimestamp].powerImportSum += powerImport
        accumulator[bucketTimestamp].powerImportCount += 1
      }

      if (powerExport !== null) {
        accumulator[bucketTimestamp].powerExportSum += powerExport
        accumulator[bucketTimestamp].powerExportCount += 1
      }

      return accumulator
    }, {})
  )
    .map((bucket) => ({
      timestamp: bucket.timestamp,
      powerImport: bucket.powerImportCount ? bucket.powerImportSum / bucket.powerImportCount : 0,
      powerExport: bucket.powerExportCount ? bucket.powerExportSum / bucket.powerExportCount : 0,
    }))
    .sort((a, b) => Number(a.timestamp) - Number(b.timestamp))
}

export function getChartScale(chartLogs) {
  const values = chartLogs.flatMap((log) => [Number(log.powerImport) || 0, Number(log.powerExport) || 0])
  const rawMin = Math.min(...values, 0)
  const rawMax = Math.max(...values, 1)
  const rawSpan = rawMax - rawMin
  const valuePadding = rawSpan === 0 ? Math.max(Math.abs(rawMax) * 0.1, 1) : rawSpan * 0.1
  const min = Math.min(0, rawMin - valuePadding)
  const max = rawMax + valuePadding

  return {
    min,
    max,
    span: Math.max(max - min, 1),
  }
}

export function getLogChartPoint(log, index, chartLogs, key, scale, dimensions = LOG_CHART_DIMENSIONS) {
  const { width, height, left, right, top, bottom } = dimensions
  const chartWidth = width - left - right
  const chartHeight = height - top - bottom
  const value = Number(log[key]) || 0
  const x = chartLogs.length === 1 ? left + chartWidth / 2 : left + (index / (chartLogs.length - 1)) * chartWidth
  const y = top + ((scale.max - value) / scale.span) * chartHeight

  return { x, y, value }
}

export function buildLogChartPath(chartLogs, key, scale, dimensions = LOG_CHART_DIMENSIONS) {
  return chartLogs
    .map((log, index) => {
      const point = getLogChartPoint(log, index, chartLogs, key, scale, dimensions)

      return `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`
    })
    .join(' ')
}

export function getStartOfLocalDay(timestamp = Date.now()) {
  const date = new Date(timestamp)

  date.setHours(0, 0, 0, 0)

  return date.getTime()
}

export function getLogRangeView(logs, range = 'day') {
  if (!logs.length) {
    return null
  }

  const config = RANGE_CONFIG[range] || RANGE_CONFIG.day
  const latestTimestamp = Math.max(...logs.map((log) => Number(log.timestamp)))
  const threshold = range === 'day' ? getStartOfLocalDay() : latestTimestamp - config.durationMs
  const filteredLogs = logs.filter((log) => Number(log.timestamp) >= threshold)

  if (range === 'day' && !filteredLogs.length) {
    return null
  }

  const sourceLogs = filteredLogs.length ? filteredLogs : logs.slice(-1)
  const descendingLogs = [...sourceLogs].sort((a, b) => Number(b.timestamp) - Number(a.timestamp))
  const chartLogs = averageLogsByBucket(sourceLogs, config.bucketMs)
  const safeChartLogs = chartLogs.length ? chartLogs : [{ timestamp: latestTimestamp, powerImport: 0, powerExport: 0 }]
  const scale = getChartScale(safeChartLogs)
  const gridLines = [0, 1, 2, 3, 4].map((line) => {
    const y = LOG_CHART_DIMENSIONS.top + (line / 4) * (LOG_CHART_DIMENSIONS.height - LOG_CHART_DIMENSIONS.top - LOG_CHART_DIMENSIONS.bottom)
    const value = scale.max - (line / 4) * scale.span

    return { y, value }
  })

  return {
    range,
    config,
    sourceLogs,
    descendingLogs,
    chartLogs: safeChartLogs,
    scale,
    gridLines,
    dimensions: LOG_CHART_DIMENSIONS,
    startLabel: formatTimestamp(range === 'day' ? threshold : sourceLogs[0]?.timestamp),
    endLabel: formatTimestamp(sourceLogs[sourceLogs.length - 1]?.timestamp),
    metaText: `${safeChartLogs.length} averaged points shown for ${range} in ${config.bucketLabel} intervals.`,
  }
}
